import { PageOrientation, Style, TableCell } from "pdfmake/interfaces";
import { TFunction } from "react-i18next";
import { MoneyApi } from "go-core/api/types";
import { estimatePdfTextWidth } from "go-list/core/components/Actions/services";
import {
	InvoiceApi,
	InvoiceCalculationType,
	InvoiceItemApi,
	TaxApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { A4WIDTH } from "../../../utils/documentPdf/consts";
import { calculateDocumentColumnWidths, formatDocumentMoney } from "../../../utils/documentPdf/documentTable";
import { INVOICE_CELL_BACKGROUND_COLOR, INVOICE_PDF_COLUMNS, INVOICE_PDF_TABLE_STYLES } from "./consts";
import { ExportInvoicePdfConfig, InvoicePdfColumn } from "./types";

export const generateInvoiceItemsTable = (
	t: TFunction,
	data: InvoiceItemApi[],
	invoice: InvoiceApi,
	config: ExportInvoicePdfConfig,
	taxes: TaxApi[]
) => {
	const columns = getInvoiceItemsTableColumns();
	const widths: (string | number)[] = [];
	const tHead: { style: Style; text: string; border: boolean[] }[] = [];
	const tableBody: TableCell[][] = [];

	columns.forEach((column) => {
		tHead.push({
			text: findNameForColumn(column, t, invoice.calculation_type === InvoiceCalculationType.GROSS),
			style: INVOICE_PDF_TABLE_STYLES.tableHeader,
			border: [false, false, false, false],
		});
		widths.push("auto");
	});
	tableBody.push(tHead);

	const table = {
		widths,
		body: tableBody,
		dontBreakRows: true,
		pageOrientation: "portrait" as PageOrientation,
		overWidth: false,
	};
	const newWidthsArray = table.widths.map(() => 0);
	const defaultMaxWidth = ((A4WIDTH - 40) * 1.5) / columns.length;
	const columnWidths: { column: InvoicePdfColumn; width: number; minWidth: number }[] = [];

	data.forEach((item, index) => {
		const bodyRow: { text: { noWrap: boolean; text: string | number }; style: Style; border: boolean[] }[] = [];
		columns.forEach((column, columnIndex) => {
			bodyRow.push({
				text: { noWrap: false, text: getValueForColumn(column, item, invoice, t, index, taxes, config.group) },
				style: {
					...INVOICE_PDF_TABLE_STYLES.tableCell,
					fillColor: index % 2 === 0 ? INVOICE_CELL_BACKGROUND_COLOR : "#fff",
				},
				border: [false, index % 2 === 0, false, index % 2 === 0],
			});

			const row = bodyRow[columnIndex];
			if (row) {
				const headLength = tHead[columnIndex]?.text.length;
				const contentLength = !row.text.text ? headLength : row.text.text.toString().length;
				const text = (headLength > contentLength ? tHead[columnIndex]?.text : row.text.text)?.toString() || "";
				if (text) {
					const width = estimatePdfTextWidth(text, headLength >= contentLength);
					if (width > defaultMaxWidth) {
						newWidthsArray[columnIndex] = defaultMaxWidth;
					} else {
						newWidthsArray[columnIndex] = Math.max(newWidthsArray[columnIndex], width);
					}

					columnWidths.push({
						column,
						minWidth:
							width > defaultMaxWidth ? defaultMaxWidth : Math.max(newWidthsArray[columnIndex], width),
						width,
					});
				}
			}
		});
		table.body.push(bodyRow);
	});

	table.widths = newWidthsArray.map((width, index) => {
		return width || table.widths[index];
	});

	const tableWidthSum: number = table.widths
		.map((width) => {
			return Number(width) || 0;
		})
		.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

	const CELL_PADDING = 7;
	const fullTableWidth = tableWidthSum + CELL_PADDING * columns.length;

	if (fullTableWidth > A4WIDTH - 40) {
		table.widths = table.widths.map((_) => {
			return "auto";
		});
	} else {
		table.widths = calculateDocumentColumnWidths(table.widths, columnWidths, flexibleColumns);
	}
	return table;
};

const flexibleColumns: InvoicePdfColumn[] = [InvoicePdfColumn.NAME, InvoicePdfColumn.QUANTITY];

export const getInvoiceItemsTableColumns = () => {
	return [...INVOICE_PDF_COLUMNS];
};

const getValueForColumn = (
	column: InvoicePdfColumn,
	item: InvoiceItemApi,
	invoice: InvoiceApi,
	t: TFunction,
	columnIndex: number,
	taxes: TaxApi[],
	grouped?: boolean
) => {
	const isTaxGross = invoice.calculation_type === InvoiceCalculationType.GROSS;
	const measureType = item.measure_type ? t(`enums.products.measure_types.${item.measure_type}`) : "";
	const defaultMoney: MoneyApi = { amount: 0, currency: "" };

	switch (column) {
		case InvoicePdfColumn.TOTAL_PRICE: {
			return isTaxGross
				? formatDocumentMoney(item.price_sum_gross || defaultMoney)
				: formatDocumentMoney(item.price_sum_net || defaultMoney);
		}
		case InvoicePdfColumn.UNIT_PRICE: {
			return isTaxGross
				? formatDocumentMoney(item.price_gross || defaultMoney)
				: formatDocumentMoney(item.price_net || defaultMoney);
		}
		case InvoicePdfColumn.TAX: {
			const taxName = taxes.find(({ id }) => Number(id) === Number(item.tax_id))?.name;
			return taxName ?? "";
		}
		case InvoicePdfColumn.QUANTITY: {
			const quantity = item.quantity || 0;
			return grouped ? quantity.toFixed(5) : quantity;
		}
		case InvoicePdfColumn.POSITION:
			return columnIndex + 1;
		case InvoicePdfColumn.NAME:
			return item.name ?? "";
		case InvoicePdfColumn.PKWIU:
			return item.pkwiu ?? "";
		case InvoicePdfColumn.UNIT:
			return measureType;
	}
};

const findNameForColumn = (column: InvoicePdfColumn, t: TFunction, isTaxGross: boolean) => {
	return getColumnsWithName(t, isTaxGross).find(({ id }) => id === column)?.name ?? "";
};

const getColumnsWithName = (t: TFunction, isTaxGross: boolean) => {
	return [
		{
			id: InvoicePdfColumn.POSITION,
			name: t("lib:go_report.word.ordinal_number_shortcut"),
		},
		{
			id: InvoicePdfColumn.NAME,
			name: t("lib:common.word.name"),
		},
		{
			id: InvoicePdfColumn.PKWIU,
			name: t("modules.invoice.field.pkwiu.title"),
		},
		{
			id: InvoicePdfColumn.UNIT,
			name: t("common.word.measure"),
		},
		{
			id: InvoicePdfColumn.QUANTITY,
			name: t("common.word.quantity"),
		},
		{
			id: InvoicePdfColumn.UNIT_PRICE,
			name: isTaxGross ? t("common.word.unit_price_gross_money") : t("common.word.unit_price_net_money"),
		},
		{
			id: InvoicePdfColumn.TOTAL_PRICE,
			name: isTaxGross
				? t("modules.invoice.field.price_sum_gross.title")
				: t("modules.invoice.field.price_sum_net.title"),
		},
		{
			id: InvoicePdfColumn.TAX,
			name: `${t("common.word.vat_rate")}\n${t("common.word.vat")}`,
		},
	];
};
